/**
 * 玩家属性面板
 * 增量更新模式 — 首次 build() 构建 DOM 结构，后续 update() 只修改变化的属性
 */

import type { PlayerState } from '../engine/Player';
import { getPlayerRealmDisplay, getCultivationProgress, getPlayerOrigin, getPlayerIdentity, isCultivationBlocked } from '../engine/Player';
import { isBountyExpired, bountyTimeLeft } from '../data/bounties';
import { getRootDisplayName } from '../data/spiritualRoots';

export class StatsPanel {
  private container: HTMLElement;
  private built = false;
  private lastName = '';
  private lastRealm = '';
  private lastRoot = '';
  private lastOrigin = '';
  private lastIdentity = '';
  private lastAttack = 0;
  private lastDefense = 0;
  private lastStones = -1;
  private lastKills = -1;
  private lastBlocked = false;
  private lastBountyFp = '';
  private lastTerritoryFp = '';

  constructor(containerId: string) {
    const el = document.getElementById(containerId);
    if (!el) throw new Error(`Element #${containerId} not found`);
    this.container = el;
    this.container.className = 'panel';
  }

  /** 首次构建 DOM 结构（仅调用一次） */
  private build(player: PlayerState): void {
    const realm = getPlayerRealmDisplay(player);
    const expProgress = getCultivationProgress(player);
    const hpPercent = (player.hp / player.maxHp) * 100;
    const mpPercent = (player.mp / player.maxMp) * 100;

    this.container.innerHTML = `
      <div class="panel-title">📋 修仙者</div>
      <div class="stat-row">
        <span class="stat-label">道号</span>
        <span class="stat-value" id="stat-name">${escapeHtml(player.name)}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">境界</span>
        <span class="stat-value realm-name" id="stat-realm">${realm}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">灵根</span>
        <span class="stat-value" id="stat-root" style="color:var(--text-gold)">${getRootDisplayName(player.spiritualRoot)}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">出身</span>
        <span class="stat-value" id="stat-origin">${getPlayerOrigin(player).name}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">身份</span>
        <span class="stat-value" id="stat-identity" style="color:var(--accent)">${getPlayerIdentity(player).name}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">生命</span>
        <div class="stat-bar">
          <div class="stat-bar-fill hp" id="stat-hp-bar" style="width:${hpPercent}%"></div>
        </div>
        <span class="stat-value" id="stat-hp-val">${player.hp}/${player.maxHp}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">灵力</span>
        <div class="stat-bar">
          <div class="stat-bar-fill mp" id="stat-mp-bar" style="width:${mpPercent}%"></div>
        </div>
        <span class="stat-value" id="stat-mp-val">${player.mp}/${player.maxMp}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">修炼</span>
        <div class="stat-bar">
          <div class="stat-bar-fill exp" id="stat-exp-bar" style="width:${expProgress}%"></div>
        </div>
        <span class="stat-value" id="stat-exp-val">${Math.floor(expProgress)}%</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">攻击</span>
        <span class="stat-value" id="stat-atk">${player.attack}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">防御</span>
        <span class="stat-value" id="stat-def">${player.defense}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">💰 灵石</span>
        <span class="stat-value" style="color:var(--text-gold)" id="stat-stones">${player.spiritStones}</span>
      </div>
      <div class="stat-row">
        <span class="stat-label">击杀数</span>
        <span class="stat-value" id="stat-kills">${player.killCount}</span>
      </div>
    `;

    // 记录初始值用于后续比对
    this.lastName = player.name;
    this.lastRealm = realm;
    this.lastRoot = getRootDisplayName(player.spiritualRoot);
    this.lastOrigin = getPlayerOrigin(player).name;
    this.lastIdentity = getPlayerIdentity(player).name;
    this.lastAttack = player.attack;
    this.lastDefense = player.defense;
    this.lastStones = player.spiritStones;
    this.lastKills = player.killCount;
    this.built = true;
  }

  /**
   * 增量更新 — 只修改变化的值。
   * 首次调用自动触发 build()。
   */
  update(player: PlayerState): void {
    if (!this.built) {
      this.build(player);
      return;
    }

    // === 高频变化：每 tick 都更新（直接赋值，同值无开销） ===

    // HP 条 + 值
    const hpBar = document.getElementById('stat-hp-bar');
    const hpVal = document.getElementById('stat-hp-val');
    if (hpBar) hpBar.style.width = `${(player.hp / player.maxHp) * 100}%`;
    if (hpVal) hpVal.textContent = `${player.hp}/${player.maxHp}`;

    // MP 条 + 值
    const mpBar = document.getElementById('stat-mp-bar');
    const mpVal = document.getElementById('stat-mp-val');
    if (mpBar) mpBar.style.width = `${(player.mp / player.maxMp) * 100}%`;
    if (mpVal) mpVal.textContent = `${player.mp}/${player.maxMp}`;

    // 修炼进度条 + 值
    const expProgress = getCultivationProgress(player);
    const expBar = document.getElementById('stat-exp-bar');
    const expVal = document.getElementById('stat-exp-val');
    if (expBar) expBar.style.width = `${expProgress}%`;
    if (expVal) expVal.textContent = `${Math.floor(expProgress)}%`;

    // === 低频变化：只在值改变时更新 ===

    // 名字
    if (player.name !== this.lastName) {
      const el = document.getElementById('stat-name');
      if (el) el.textContent = player.name;
      this.lastName = player.name;
    }

    // 境界
    const realm = getPlayerRealmDisplay(player);
    if (realm !== this.lastRealm) {
      const el = document.getElementById('stat-realm');
      if (el) el.textContent = realm;
      this.lastRealm = realm;
    }

    // 灵根
    const rootName = getRootDisplayName(player.spiritualRoot);
    if (rootName !== this.lastRoot) {
      const el = document.getElementById('stat-root');
      if (el) el.textContent = rootName;
      this.lastRoot = rootName;
    }

    // 出身
    const originName = getPlayerOrigin(player).name;
    if (originName !== this.lastOrigin) {
      const el = document.getElementById('stat-origin');
      if (el) el.textContent = originName;
      this.lastOrigin = originName;
    }

    // 身份
    const identityName = getPlayerIdentity(player).name;
    if (identityName !== this.lastIdentity) {
      const el = document.getElementById('stat-identity');
      if (el) el.textContent = identityName;
      this.lastIdentity = identityName;
    }

    // 攻击
    if (player.attack !== this.lastAttack) {
      const el = document.getElementById('stat-atk');
      if (el) el.textContent = String(player.attack);
      this.lastAttack = player.attack;
    }

    // 防御
    if (player.defense !== this.lastDefense) {
      const el = document.getElementById('stat-def');
      if (el) el.textContent = String(player.defense);
      this.lastDefense = player.defense;
    }

    // 灵石
    if (player.spiritStones !== this.lastStones) {
      const el = document.getElementById('stat-stones');
      if (el) el.textContent = String(player.spiritStones);
      this.lastStones = player.spiritStones;
    }

    // 修炼冻结警告
    const blocked = isCultivationBlocked(player);
    if (blocked !== this.lastBlocked) {
      const warnEl = document.getElementById('stat-block-warn');
      if (blocked) {
        const mission = player.mandatoryMission;
        const el = document.createElement('div');
        el.id = 'stat-block-warn';
        el.style.cssText = 'color:var(--danger);font-size:0.8em;text-align:center;padding:6px;margin-top:4px;background:rgba(200,50,50,0.1);border:1px solid rgba(200,50,50,0.3);border-radius:4px;';
        el.textContent = `⚠️ 挂机冻结：${mission?.desc ?? '未完成强制任务'}`;
        this.container.appendChild(el);
      } else {
        if (warnEl) warnEl.remove();
      }
      this.lastBlocked = blocked;
    }

    // 悬赏状态
    const bountyFp = player.bounty ? `${player.bounty.enemyName}|${bountyTimeLeft(player.bounty)}` : '';
    if (bountyFp !== this.lastBountyFp) {
      const el = document.getElementById('stat-bounty');
      if (player.bounty && !isBountyExpired(player.bounty)) {
        const b = player.bounty;
        const html = `<div id="stat-bounty" style="margin-top:6px;padding:6px;background:rgba(200,160,60,0.08);border:1px solid rgba(200,160,60,0.2);border-radius:4px;font-size:0.82em;text-align:center;">
          🏹 悬赏：<b style="color:var(--text-gold);">${b.enemyName}</b> 📍${b.zoneName} ⏱${bountyTimeLeft(b)}<br>
          <span style="font-size:0.85em;">💰${b.stoneReward} 📖${b.expReward} 🌿${b.herbReward} 💎${b.coreReward}</span></div>`;
        if (el) el.outerHTML = html;
        else this.container.insertAdjacentHTML('beforeend', html);
      } else {
        if (el) el.remove();
      }
      this.lastBountyFp = bountyFp;
    }

    // 灵脉状态
    const territoryFp = player.territory ? `${player.territory.veinName}|${player.territory.veinBonus}` : '';
    if (territoryFp !== this.lastTerritoryFp) {
      const el = document.getElementById('stat-territory');
      if (player.territory) {
        const t = player.territory;
        const html = `<div id="stat-territory" style="margin-top:4px;padding:6px;background:rgba(100,200,100,0.08);border:1px solid rgba(100,200,100,0.2);border-radius:4px;font-size:0.82em;text-align:center;">
          ⛏️ ${t.veinName} 修炼+${Math.round(t.veinBonus*100)}%</div>`;
        if (el) el.outerHTML = html;
        else this.container.insertAdjacentHTML('beforeend', html);
      } else {
        if (el) el.remove();
      }
      this.lastTerritoryFp = territoryFp;
    }

    // 击杀数
    if (player.killCount !== this.lastKills) {
      const el = document.getElementById('stat-kills');
      if (el) el.textContent = String(player.killCount);
      this.lastKills = player.killCount;
    }
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
